import { Box, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import Explanation from "../../components/Explanation";
import Control from "../../components/Control";
import { useAudio } from "../../../Providers/AudioProvider";

import QuestionHeader from "../../components/QuizHeader";
import QuizXpPoint from "../../components/QuizXpPoint";
import QuizHint from "../../components/QuizHint";
import { BaseQuizProps } from "./types";
import DragAndDrop from "../DragAndDrop";
import { UniqueIdentifier } from "@dnd-kit/core";
import { swapKeyValue } from "./helper";
import _ from "lodash";
import Toaster from "../../components/Toaster";
import { ModuleType } from "../../../internal/courses/api";

interface QuizState {
  value: Record<UniqueIdentifier, UniqueIdentifier | null>;
  swapedValue: Record<UniqueIdentifier, UniqueIdentifier | null>;
  showExplanation: boolean;
  feedback: string;
  showCorrectLocation: boolean;
  showXpPoints: boolean;
  showTryAgainButton: boolean;
  showCheckButton: boolean;
  showResetButton: boolean;
  showHint: boolean;
  dropHightLight: "neutral" | "warning" | "success";
}

export interface DragAndDropQuizProps {
  hint: string | null;
  draggableItems: {
    id: string;
    label: string;
  }[];
  question: IConfig[];
  explanation: IConfig[];
  containers: string[];
  template: string;
  itemHeight?: string | number;
  itemWidth?: string | number;
  gridColumns?: number;
  correctLocations: { containerId: string; draggableItemId: string }[];
  onChange?:
    | ((
        itemParents: Record<UniqueIdentifier, UniqueIdentifier | null>,
        parentsByItems: Record<UniqueIdentifier, UniqueIdentifier | null>
      ) => void)
    | any;
  status: BaseQuizProps["status"];
  contentAPI: BaseQuizProps["contentAPI"];
  xpPoints: BaseQuizProps["xpPoints"];
  meta: BaseQuizProps["meta"];
  moduleType: ModuleType;
}
type CorrectLocationItem = {
  containerId: string;
  draggableItemId: string;
};

const mapCorrectLocations = (
  correctLocationsArray: CorrectLocationItem[]
): Record<string, string> => {
  return correctLocationsArray.reduce<Record<string, string>>(
    (acc, { containerId, draggableItemId }) => {
      acc[containerId] = draggableItemId;
      return acc;
    },
    {}
  );
};

// Component
const DragAndDropQuiz = (props: DragAndDropQuizProps) => {
  const {
    question,
    explanation,
    draggableItems,
    onChange,
    status = "not-started",
    contentAPI,
    xpPoints,
    template,
    meta,
    itemHeight,
    itemWidth,
    hint,
    gridColumns,
    correctLocations,
    moduleType,
  } = props;
  const [quizState, setQuizState] = useState<QuizState>({
    value: {},
    swapedValue: {},
    showExplanation: false,
    feedback: "Incorrect, try again.",
    showHint: false,
    showTryAgainButton: false,
    showCorrectLocation: false,
    showCheckButton: true,
    showXpPoints: false,
    showResetButton: false,
    dropHightLight: "neutral",
  });
  const toast = useToast();
  const { playWrongOption, playCorrectSound } = useAudio();

  const correctLocationsObj = mapCorrectLocations(correctLocations);

  useEffect(() => {
    if (meta && meta.showXpPoints) {
      setQuizState((p) => ({ ...p, showXpPoints: true }));
    }
  }, [meta]);

  // Effects
  useEffect(() => {
    if (onChange && quizState.value) {
      onChange(quizState.value);
    }
    setQuizState((p) => ({ ...p, swapedValue: swapKeyValue(quizState.value) }));
  }, [quizState.value, onChange]);

  useEffect(() => {
    if (status === "completed") {
      setQuizState((prev) => ({
        ...prev,
        value: correctLocationsObj,
        showCheckButton: false,
        showCorrectLocation: true,
      }));
    }
  }, [status, correctLocations]);

  const checkIsCorrectOption = () => {
    const swappedValues = swapKeyValue(quizState.value);
    return _.isEqual(swappedValues, correctLocationsObj);
  };

  const contentAPIComplete = () => {
    contentAPI.completed({ showXpPoints: true });
  };

  const onCorrect = () => {
    setQuizState((prev) => ({
      ...prev,
      isSelectedCorrect: true,
      showCorrectLocation: true,
      showHint: false,
      showCheckButton: false,
      showXpPoints: true,
      dropHightLight: "success",
    }));
    contentAPIComplete();
    playCorrectSound(0.6);
  };

  const onWrong = () => {
    playWrongOption(0.9);
    setQuizState((prev) => ({
      ...prev,
      showCheckButton: false,
      showTryAgainButton: true,
      showHint: false,
      dropHightLight: "warning",
    }));
    toast({
      id: "quiz_feedback",
      description: hint,
      status: "warning",
      duration: 9000,
      isClosable: true,
      render: (props) => (
        <Toaster onClose={props.onClose} feedback={hint ?? ""} />
      ),
    });
  };

  const handleCheck = () => {
    const isCorrect = checkIsCorrectOption();

    if (isCorrect) {
      onCorrect();
    } else {
      onWrong();
    }
  };

  const handleExplanation = () => {
    playWrongOption(0.9);
    if (!quizState.showCorrectLocation) {
      setQuizState((prev) => ({
        ...prev,
        showCorrectLocation: true,
        value: swapKeyValue(correctLocationsObj) as any,
        showHint: false,
        dropHightLight: "warning",
        showCheckButton: false,
        showTryAgainButton: false,
      }));
    }
    // save the event of explanaiton
    if (
      !quizState.showExplanation &&
      !quizState.showCorrectLocation &&
      status !== "completed"
    ) {
      contentAPI.explanation_viewed();
    }

    setQuizState((prev) => ({
      ...prev,
      showExplanation: !prev.showExplanation,
    }));
  };

  const handleReset = () => {
    setQuizState({
      value: {},
      swapedValue: {},
      showCorrectLocation: false,
      showExplanation: false,
      feedback: "Incorrect, try again.",
      showTryAgainButton: false,
      showHint: false,
      showCheckButton: true,
      showXpPoints: false,
      dropHightLight: "neutral",
      showResetButton: false,
    });
  };

  const handleTryAgain = () => {
    setQuizState((p) => ({
      ...p,
      showCheckButton: true,
      showTryAgainButton: false,
    }));
  };

  const handleOnChange = useCallback(
    (itemParents: Record<UniqueIdentifier, UniqueIdentifier | null>) => {
      toast.closeAll();
      if (!quizState.showCorrectLocation) {
        setQuizState((prev) => ({
          ...prev,
          value: itemParents,
          showCheckButton: true,
          showTryAgainButton: false,
          dropHightLight: "neutral",
        }));
      }
    },
    [quizState.value]
  );

  // const getCurrentDropIndex = useCallback(() => {
  //   if (containers.length === 1) {
  //     const index = draggableItems.findIndex(
  //       (item) => item.id === quizState.swapedValue[containers[0]]
  //     );
  //     return index === -1 ? 0 : index + 1;
  //   }
  //   return 0;
  // }, [containers, draggableItems, quizState.swapedValue]);

  const renderDragAndDrop = () => {
    return (
      <DragAndDrop
        value={quizState.value}
        draggableItems={draggableItems}
        template={template}
        containers={correctLocations.map((l) => l.containerId)}
        itemWidth={itemWidth}
        itemHeight={itemHeight}
        gridColumns={gridColumns}
        disabled={quizState.showCorrectLocation}
        onChange={handleOnChange}
        highlight={quizState.dropHightLight}
        highlightTemplate={true}
      />
    );
  };

  return (
    <Box
      px={{ base: "1rem", lg: "none" }}
      width={{ base: "100%", lg: "31rem" }}
      mx="auto"
    >
      <Box
        border="1px solid"
        borderRadius="1rem"
        borderColor={
          moduleType === ModuleType.content ? "neutral.800" : "transparent"
        }
        width="100%"
        py="1rem"
      >
        <QuestionHeader question={question} />

        {renderDragAndDrop()}

        {/* Explanation Section */}
        <Explanation
          explanantion={explanation}
          showExplanation={quizState.showExplanation}
        />

        {/* Controls */}
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          px="1rem"
        >
          <Control
            showCheck={quizState.showCheckButton}
            isCheckDisabled={!quizState.value}
            showTryAgain={quizState.showTryAgainButton}
            showExplanation={quizState.showExplanation}
            showReset={quizState.showResetButton}
            explanantionOnClick={handleExplanation}
            onCheckClick={handleCheck}
            resetOnClick={handleReset}
            onTryAgainClick={handleTryAgain}
          />

          <QuizXpPoint
            showXpPoints={quizState.showXpPoints}
            xpPoints={xpPoints}
          />
        </Box>
      </Box>
      <QuizHint
        hint={hint}
        isHintShowed={quizState.showHint}
        onClick={() => {
          setQuizState((p) => ({ ...p, showHint: false }));
          toast.closeAll();
        }}
      />
    </Box>
  );
};

export default DragAndDropQuiz;
